/* eslint-disable @typescript-eslint/no-explicit-any */
import CountUp from 'react-countup';
import { LegacyRef } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';

import paymentIcon from 'assets/img/icons/payment-icon.svg';
import contractIcon from 'assets/img/icons/contract-icon.svg';
import dashboardService from 'service/dashboard';
import styles from './styles.module.scss';
import { useAppContext } from 'providers/AppProvider';
import { useWindowWidth } from 'utils/useWindowSize';
import { PaymentMiniIcon, ContractMiniIcon } from 'assets/img/icons/icons';

interface PaymentAndTransactionProps {
  selectedCurrencyId?: any;
  dateFrom?: any;
  dateTo?: any;
  selectedBranchId?: string;
}

const PaymentAndTransaction = ({
  selectedCurrencyId,
  dateFrom,
  dateTo,
  selectedBranchId
}: PaymentAndTransactionProps) => {
  const { t, i18n } = useTranslation();
  const windowSize: any = useWindowWidth();
  const {
    config: { theme }
  } = useAppContext();

  //Get RATING
  const { data } = useQuery(
    ['GET_RATING', dateFrom, dateTo, selectedCurrencyId, selectedBranchId],
    () => {
      if (selectedCurrencyId !== '' && dateFrom && selectedBranchId !== '') {
        const res = dashboardService
          .getRating({
            offset: 0,
            limit: 10,
            date_from: dateFrom,
            date_to: dateTo,
            currency_pid: selectedCurrencyId,
            branch_pid: selectedBranchId
          })
          .then((res: any) => res);
        return res;
      }
    },
    {
      enabled: Boolean(dateFrom && selectedCurrencyId && selectedBranchId)
    }
  );

  const formattedAmount = (amount: number) =>
    new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 4,
      useGrouping: true
    })
      .format(amount)
      .replace(/,/g, ' ');

  return (
    <div className={theme === 'dark' ? styles.PTInfoDark : styles.PTInfo}>
      <div className={styles.PTBox}>
        <div className={styles.topInfo}>
          {windowSize <= 768 && (
            <span className={styles.miniIcon}>
              <PaymentMiniIcon />
            </span>
          )}
          <div className={styles.paymenInfo}>
            <p className={styles.topTitle}>{t('payments')}</p>
            <CountUp
              end={data?.payments?.total_amount ?? 0}
              duration={2.75}
              suffix={+selectedCurrencyId === 2 ? ` ${t('sum')}` : ' $'}
              separator=" "
              delay={0}
            >
              {({ countUpRef }) => (
                <div>
                  <span
                    className={styles.topCount}
                    ref={countUpRef as LegacyRef<HTMLHeadingElement>}
                  ></span>
                </div>
              )}
            </CountUp>
          </div>
          {windowSize > 768 && <img src={paymentIcon} alt="icon" />}
        </div>
        {data?.payments?.payment_methods?.map((items: any) => (
          <div key={items} className={styles.bottomInfo}>
            <ul className={styles.bTitles}>
              <li>{items?.payment_method?.name?.[i18n?.language]}</li>
            </ul>

            <ul className={styles.bCounts}>
              <li>
                {items?.amount ? formattedAmount(items?.amount) : '0'}{' '}
                {t('sum')}
              </li>
            </ul>
          </div>
        ))}
      </div>

      <div className={styles.PTBox}>
        <div className={styles.topInfo}>
          {windowSize <= 768 && (
            <span className={styles.miniIcon}>
              <ContractMiniIcon />
            </span>
          )}
          <div className={styles.paymenInfo}>
            <p className={styles.topTitle}>{t('transactions')}</p>
            <CountUp
              end={data?.transactions?.total_amount ?? 0}
              duration={2.75}
              suffix={` ${t('things')}`}
              separator=" "
              delay={0}
            >
              {({ countUpRef }) => (
                <div>
                  <span
                    className={styles.topCount}
                    ref={countUpRef as LegacyRef<HTMLHeadingElement>}
                  ></span>
                </div>
              )}
            </CountUp>
          </div>
          {windowSize > 768 && <img src={contractIcon} alt="icon" />}
        </div>
        {data?.transactions?.product_kind?.map((items: any) => (
          <div key={items} className={styles.bottomInfo}>
            <ul className={styles.bTitles}>
              <li>{items?.product_kind?.name?.[i18n?.language]}</li>
            </ul>

            <ul className={styles.bCounts}>
              <li>
                {Number.isInteger(items?.amount)
                  ? items?.amount?.toString()
                  : items?.amount?.toFixed(4) ?? 0}{' '}
                {t('things')}
              </li>
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PaymentAndTransaction;
